import React, { createContext, useContext, useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Leaf, Lock, ArrowRight, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attach JWT from localStorage to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("aura_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("aura_token");
    if (!token) {
      setUser(false);
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("aura_token");
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("aura_token", data.access_token);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem("aura_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3 text-[#5C6B5A]"
        style={{ backgroundColor: "#F4EFE7" }}
        data-testid="auth-loading"
      >
        <RefreshCw className="h-6 w-6 animate-spin text-[#8A9A86]" />
        <span className="text-sm">Verifying your aura credentials...</span>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 font-body text-[#2A3B28]"
      style={{ backgroundColor: "#F4EFE7" }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="flex items-center justify-center gap-2 mb-10 text-[#2A3B28]"
          data-testid="login-logo-home-link"
        >
          <Leaf className="h-6 w-6 text-[#8A9A86] stroke-[1.5]" />
          <span className="text-2xl font-headings font-normal tracking-tight">InnerAura</span>
        </Link>

        <div className="bg-[#FBF7F0] rounded-3xl border border-[#D7CCBE] shadow-sm p-8 md:p-10 space-y-7">
          <div className="space-y-2 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#E9E1D6] flex items-center justify-center">
              <Lock className="h-5 w-5 text-[#8A9A86]" />
            </div>
            <h1 className="text-2xl font-headings font-normal tracking-tight">Admin Portal</h1>
            <p className="text-sm text-[#5C6B5A]">
              Secure access to the InnerAura waitlist registry.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B5A]">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@inneraura.com"
                data-testid="login-email-input"
                className="w-full bg-[#F1EDE4] rounded-xl border border-[#D7CCBE] px-4 py-3 text-[#2A3B28] placeholder:text-[#8A9B84] focus:outline-none focus:ring-2 focus:ring-[#8A9A86]/30 transition-all text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6B5A]">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="w-full bg-[#F1EDE4] rounded-xl border border-[#D7CCBE] px-4 py-3 text-[#2A3B28] placeholder:text-[#8A9B84] focus:outline-none focus:ring-2 focus:ring-[#8A9A86]/30 transition-all text-sm"
              />
            </div>

            {error && (
              <p
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-button"
              className="w-full bg-[#2A3B28] hover:bg-[#1C281B] text-white font-medium rounded-xl px-6 py-3.5 transition-all duration-300 ease-out shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? "Signing in..." : "Sign In"}
              {!isLoading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <Link
            to="/"
            className="block text-center text-xs text-[#8A9B84] hover:text-[#2A3B28] transition-colors"
            data-testid="login-back-link"
          >
            ← Back to landing page
          </Link>
        </div>
      </div>
    </div>
  );
}
